import { LuneTheme } from '@lune-fe/lune-ui-lib'
import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import { FC, ReactNode } from 'react'

import Divider from '@/components/Divider'

import '@fontsource/space-grotesk'

import '@fontsource/space-grotesk/700.css'

export interface MobileMapLegendBoxProps {
    items: {
        label: ReactNode
        icon: ReactNode
    }[]
    total?: string
    sx?: SxProps
    calculateItemsFontWeight?: (isFirstItem: boolean) => string
}

/**
 * Render a legend card. Interactive checkout uses this for its mobile carousel.
 * @param items - bullet points for the card
 * @param total - total tCO₂e for the card. Optional
 * @param sx - custom styles for the card applied to the root Box. Optional
 * @param calculateItemsFontWeight - function to calculate the font weight of the bullet points.
 * Accepts a boolean indicating if the item is the first item in the list. Optional
 *
 */
const MobileMapLegendBox: FC<MobileMapLegendBoxProps> = ({
    items,
    total,
    sx,
    calculateItemsFontWeight,
}) => {
    const { palette } = LuneTheme

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                fontFamily: 'Space Grotesk',
                p: 3,
                backgroundColor: `white`,
                color: palette.Blue500,
                border: '1px solid',
                borderColor: palette.Blue500,
                borderRadius: '24px',
                width: '100%',
                gap: 1,
                fontSize: '14px',
                height: '100%',
                svg: {
                    width: '16px',
                },
                ...(sx ?? {}),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    justifyContent: 'center',
                    gap: 1,
                }}
            >
                {items.map(({ label, icon }, index) => (
                    <Box
                        key={`m-${index}`}
                        sx={{
                            fontWeight: calculateItemsFontWeight
                                ? calculateItemsFontWeight(index === 0)
                                : `initial`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'start',
                            gap: 2,
                        }}
                    >
                        {icon}
                        {label}
                    </Box>
                ))}
            </Box>
            {total && (
                <Box sx={{ width: '100%' }}>
                    <Divider />
                    <Box sx={{ fontWeight: 700, mt: 1 }}>Calculated {total} tCO₂e</Box>
                </Box>
            )}
        </Box>
    )
}

export default MobileMapLegendBox
